import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { storagePrefix } from '../utils/storage-key';

/** Internal per-job cache entry. */
interface CacheEntry {
  /** Defendant indices that passed verification for this job. */
  indices: number[];
  /** Unix timestamp (ms) when the first entry for this job was recorded. */
  timestamp: number;
}

/** Prefix used for cache keys read/written before any user has logged in. */
const NO_ENTITY_PREFIX = 'anon';

/**
 * Persisted cache for document verification results.
 *
 * @remarks
 * Allows a Process Server to navigate away from the `/verify/:id` route
 * and back without having to re-scan verification codes, provided they
 * return within the same session and within 24 hours.
 *
 * **Persistence:** Written to `localStorage` after every mutation so the
 * verified state survives a browser hard refresh (page reload). Restored
 * automatically on service construction.
 *
 * **Expiry rules** (per Prakash's spec):
 * - Attempt/Completion submitted → `clearJob()` clears the specific job entry.
 * - User logs out → `clearAll()` clears every entry (in-memory + localStorage).
 * - 24 hours elapsed since first scan → reads return empty / false.
 *
 * **Per-server (entity) scoping (2026-07-24 fix):** every internal cache key
 * is `${storagePrefix(entityId)}:${jobId}`, not a bare `jobId` — mirroring the
 * `storagePrefix(entityId)` convention every other per-user cache in this app
 * already uses (`{hash}_cache_jobs`, `{hash}_queue_submissions`, etc., see
 * `storage-key.ts`). This was previously the one cache in the app keyed only
 * by `jobId`, relying entirely on the `clearAll()` logout effect below for
 * cross-user isolation. That effect only fires on a `true → false` transition
 * of `AuthService.isAuthenticated()` — but `AuthService.login()` does not log
 * out the previous session first, it just replaces `_token` directly, so a
 * login that happens while a *different, still-valid* token is present (no
 * intervening logout) leaves `isAuthenticated()` at `true` the whole time and
 * the effect never re-runs. Reachable in practice on a shared device if
 * `login()` is ever invoked without going through the normal logout flow
 * first (the `/auth` route's `guestGuard` blocks this via normal navigation,
 * but nothing at the service layer itself prevented it). Entity-scoping the
 * keys closes this regardless of *how* the token was swapped: Officer B's
 * reads/writes use a different key than Officer A's, so nothing can leak
 * across a login even if the logout-clear effect is ever bypassed. The
 * logout-clears-everything effect is kept as-is (defense in depth / general
 * hygiene), but is no longer the only thing preventing cross-user leakage.
 */
@Injectable({ providedIn: 'root' })
export class VerificationCacheService {
  private readonly EXPIRY_MS   = 24 * 60 * 60 * 1000;
  private readonly STORAGE_KEY = '_verif_cache';

  private readonly auth  = inject(AuthService);
  private readonly cache = signal<Map<string, CacheEntry>>(new Map());

  constructor() {
    // Restore any previously verified state from localStorage so the PS does
    // not need to re-scan after a browser hard refresh.
    this.restore();

    // Clear all cached codes on logout so stale verifications cannot persist
    // across user sessions on a shared device. Kept as defense-in-depth
    // alongside the per-entity key scoping below (see class doc).
    effect(() => {
      if (!this.auth.isAuthenticated()) {
        this.clearAll();
      }
    });
  }

  /**
   * Builds the entity-scoped internal cache key for `jobId` from the
   * currently logged-in process server's entity ID.
   *
   * @remarks
   * Uses the opaque {@link storagePrefix} hash (never the raw entity ID) so
   * the server ID isn't visible in plaintext `localStorage`, matching every
   * other scoped cache key in this app. Falls back to a fixed `'anon'`
   * prefix when no token is present (not expected in practice — this
   * service's callers only run on authenticated routes — but keeps the
   * method total rather than throwing).
   *
   * @param jobId - Titanium Legal job ID.
   */
  private scopedKey(jobId: string): string {
    const entityId = this.auth.token()?.user.entityId;
    const prefix = entityId != null ? storagePrefix(entityId) : NO_ENTITY_PREFIX;
    return `${prefix}:${jobId}`;
  }

  /**
   * Records that defendant `defIndex` passed verification for `jobId`.
   * If an existing entry for this job has expired, it is replaced with a
   * fresh entry so the 24-hour window restarts. Persists to `localStorage`.
   *
   * @param jobId    - Titanium Legal job ID (e.g. `'TL-2241'`).
   * @param defIndex - Zero-based defendant index within the job.
   */
  markVerified(jobId: string, defIndex: number): void {
    const key = this.scopedKey(jobId);
    this.cache.update(map => {
      const next = new Map(map);
      const entry = next.get(key);
      if (entry && !this.isExpired(entry)) {
        next.set(key, {
          indices:   [...new Set([...entry.indices, defIndex])],
          timestamp: entry.timestamp,
        });
      } else {
        next.set(key, { indices: [defIndex], timestamp: Date.now() });
      }
      return next;
    });
    this.persist();
  }

  /**
   * Returns `true` when defendant `defIndex` is cached as verified for `jobId`
   * under the currently logged-in server's scope, and the cache entry has
   * not expired.
   *
   * @param jobId    - Titanium Legal job ID.
   * @param defIndex - Zero-based defendant index.
   */
  isVerified(jobId: string, defIndex: number): boolean {
    const entry = this.cache().get(this.scopedKey(jobId));
    if (!entry || this.isExpired(entry)) return false;
    return entry.indices.includes(defIndex);
  }

  /**
   * Returns all cached verified defendant indices for `jobId` under the
   * currently logged-in server's scope. Returns an empty array when the job
   * is not in the cache (including when it was cached by a *different*
   * server ID) or the entry has expired.
   *
   * @param jobId - Titanium Legal job ID.
   */
  getVerifiedIndices(jobId: string): number[] {
    const entry = this.cache().get(this.scopedKey(jobId));
    if (!entry || this.isExpired(entry)) return [];
    return [...entry.indices];
  }

  /**
   * Removes the cache entry for a specific job (scoped to the currently
   * logged-in server) and updates `localStorage`. Call this after an attempt
   * or completion is successfully submitted so the verification code cannot
   * be reused for a future service attempt.
   *
   * @param jobId - Titanium Legal job ID to evict.
   */
  clearJob(jobId: string): void {
    const key = this.scopedKey(jobId);
    this.cache.update(map => {
      const next = new Map(map);
      next.delete(key);
      return next;
    });
    this.persist();
  }

  /**
   * Removes all cached entries from memory and `localStorage`.
   * Called automatically when the user logs out.
   */
  clearAll(): void {
    this.cache.set(new Map());
    try { localStorage.removeItem(this.STORAGE_KEY); } catch { /* unavailable */ }
  }

  /** Returns `true` when `entry` was created more than 24 hours ago. */
  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > this.EXPIRY_MS;
  }

  /**
   * Writes the current cache snapshot to `localStorage` as a JSON array of
   * `[scopedKey, CacheEntry]` tuples (`scopedKey` is `${entityPrefix}:${jobId}`
   * — see {@link scopedKey}). Non-fatal on quota errors.
   */
  private persist(): void {
    try {
      const entries = [...this.cache().entries()];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(entries));
    } catch { /* storage quota exceeded or unavailable */ }
  }

  /**
   * Reads the `localStorage` snapshot written by {@link persist} and
   * populates the in-memory cache. Non-fatal on corrupted or missing data.
   */
  private restore(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const entries = JSON.parse(raw) as [string, CacheEntry][];
      this.cache.set(new Map(entries));
    } catch { /* corrupted data — start with empty cache */ }
  }
}
